<template>
  <GcBaseLayout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="backButtonRoute ? true : false"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
    :tabs="getTabs"
    :selectedIndex="getSelectedIndex"
    ref="gc-base-layout"
    class="touch-sales-order-layout stock-take-count"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="'Stocktake'"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="'Stocktake #' + baseView.id"
        :secondaryLabel="undefined"
      />
    </template>

    <div class="css-v2 transaction-screen" v-if="loaded">
      <div class="gc-header-toolbar">
        <gc-form-header-toolbar
          :toolbar-options="getHeaderToolbar"
          :data-model="baseView.mode != 'create' ? toolBarModel : {}"
          :backRoute="backButtonRoute"
          :setLoading="showLoading"
          ref="gc-form-header-toolbar"
          :baseView="baseView"
          :customButtons="getHeaderToolbar.customButtons"
        ></gc-form-header-toolbar>
      </div>

      <div class="gc-details-grid">
        <gc-filter-split-view
          :showLeftContainer="showLeftContainer"
          :showRightContainer="showRightContainer"
          :rightContainerWidth="250"
        >
          <template #leftContent>
            <dx-data-grid
              @toolbar-preparing="toolbarPreparing($event)"
              :height="getGridHeight"
              :columns="getColumns"
              :data-source="getDataSource"
              ref="dx-data-grid-comp"
              :key-expr="'ID'"
              :repaint-changes-only="true"
              :show-borders="true"
              :two-way-binding-enabled="false"
              :focusedRowEnabled="true"
              :column-chooser="{ enabled: true }"
              :selection="{
                mode: Status != 'C' && getUserAccess ? 'multiple' : 'none',
                selectAllMode: 'allPages',
                showCheckBoxesMode: 'always',
              }"
              @content-ready="onContentReady"
              @selection-changed="onStocktakeGridSelectionChanged"
              :pager="{ visible: false }"
            >
              <DxScrolling
                :mode="'virtual'"
                :row-rendering-mode="'virtual'"
                :preloadEnabled="true"
              />
              <template #Record="{ data }">
                <GcRecord
                  :data="data"
                  :primary="data.column['cellTemplateRecord']['primary']"
                  :secondary="data.column['cellTemplateRecord']['secondary']"
                />
              </template>

              <template #Decimal="{ data }">
                <div
                  v-if="data.column.dataField === 'Count' && data.value !== 0"
                >
                  <a @click="onCountHyperlinkClicked(data)">
                    <GcDecimal :dataValue="data" :format="data.column.format"
                  /></a>
                </div>
                <div v-else>
                  <GcDecimal :dataValue="data" :format="data.column.format" />
                </div>
              </template>
              <template #DecimalCell="{ data }">
                <GcDecimalCell :dataValue="data" :format="data.column.format" />
              </template>
              <template #count-status-template="{ data }">
                <gc-warning-icon
                  v-if="data.data.CountStatus == 'Pending'"
                  @clicked="() => {}"
                />
              </template>

              <template #menu-cell-template="{ data }">
                <GcMenuCellTemplate
                  class="invoice-print-btn"
                  :data="data"
                  :menuOptions="data.column.ellipsisMenu"
                  :onItemClickCallBack="onItemClickCallBack"
                  :visible="data.column.visible"
                  :disabled="
                    data.column.makeDisabled
                      ? data.column.makeDisabled(data)
                      : false
                  "
                />
              </template>
            </dx-data-grid>

            <GcFooter v-if="showFooter">
              <template #rightContent>
                <div
                  class="gc-grid-footer-item"
                  v-for="item in footerRightItems"
                  :key="item.label"
                >
                  <label class="gc-footer-label">{{ item.label }}</label>
                  <GcFooterItem
                    :value="item.value"
                    :format="item.format"
                    :length="item.format == 'Decimal-16-4C' ? 8 : 6"
                  />
                </div>
              </template>
            </GcFooter>
          </template>

          <template #rightContent>
            <gc-filter-panel
              :toolbarVisible="true"
              :toolbar="filterToolbar"
              :formItemData="getFilterFormItems"
              :setGridPanelFilter="setGridPanelFilter"
              :gridFoolter="false"
              :showLoading="showLoading"
              :from="'transaction-list-grid'"
              :isNormal="false"
              :persistenceKey="undefined"
              :key="'fp-call-schedule'"
              :overWriteClear="true"
              :heightAdjustment="55"
            ></gc-filter-panel>
          </template>
        </gc-filter-split-view>
      </div>
    </div>

    <GcPopupStockTakeCount
      v-if="visibleCountInfo"
      :popupVisible.sync="visibleCountInfo"
      :countData="countData"
      :showLoading="showLoading"
      :displayServerError="displayServerError"
      :recallFromCount="recallFromCount"
    />
  </GcBaseLayout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import DataGridMixin from "@/_mixins/data-grid.mixin";
import {
  GetIndexByTabName,
  StockTakeTabs,
  StockTakeTabsVisibility,
} from "../../../_models/tabdata/inventory/stock-take";
import { mapGetters } from "vuex";
import ArrayStore from "devextreme/data/array_store";
import { StocktakeReviewService } from "../../../_services/api/inventory/stocktake-review.service";
import ColumnWidth from "../../../_helpers/data-grid/ColumnWidth";
import { FeatureStorage } from "../../../_services/storage";
import { getGlobalField } from "../../../_store/helpers";
import { StocktakePrepareService } from "../../../_services/api/inventory/stocktake-prepare.service";

import GcPopupStockTakeCount from "../../../popups/stock-take/gc-popup-stocktake-count";
import { StockTakeListService } from "../../../_services/api/inventory/stock-take-list.service";

import gcWarningIcon from "../../../components/gc-warning-icon.vue";

export default {
  mixins: [RouteMixin, FormMixin, DataGridMixin],
  components: { GcPopupStockTakeCount, gcWarningIcon },
  data() {
    return {
      loaded: false,
      reviewDataSource: new ArrayStore({
        key: "ID",
        data: [],
      }),
      showLeftContainer: true,
      showRightContainer: true,
      stocktakeGridSelectedData: [],
      filterToolbar: {
        close: {
          icon: "showpanel",
          onClick: () => {
            this.closeFilterPanel();
          },
        },
      },
      showFooter: true,
      footerItems: [],
      Status: undefined,
      visibleCountInfo: false,
      countData: {},
      toolBarModel: {
        Status: undefined,
      },
    };
  },
  async beforeMount() {
    await StocktakePrepareService.getStockTakeListItemById(this.baseView.id, [
      "Code",
      "Status",
      "FriendlyStatus",
    ])
      .then((res) => {
        let data = res.data.value[0];
        if (data) {
          this.Status = data.Status;
          this.toolBarModel.Status = data.FriendlyStatus;
        }
      })
      .catch(() => {
        this.showLoading(false);
      });

    StockTakeTabsVisibility({
      ...this.baseView,
      ...{ isEditable: this.isEditable },
    });
  },
  async created() {
    this.loaded = true;
    this.$nextTick(() => {
      this.setFooterItems([]);
      // var item = document.getElementById("filter-btn");
      // item.classList.add("dx-state-invisible");
    });
  },
  computed: {
    ...mapGetters({ gridHeight: "ui/getHeightForDataGrid" }),
    getGridHeight() {
      return this.gridHeight - 20 - 40 - 55;
      // 20 => Top & Bottom Padding
    },
    backButtonRoute() {
      return "/stocktake";
    },
    getTabs() {
      return StockTakeTabs({
        ...this.baseView,
        ...{ isEditable: this.isEditable && this.getUserAccess },
      });
    },
    getSelectedIndex() {
      return GetIndexByTabName("Review", { ...this.baseView });
    },
    getColumns() {
      return this.parseGridColumns(this.columns);
    },
    getDataSource() {
      return StocktakeReviewService.dataSourceConfig(
        "api/StocktakeReviewList",
        {
          filter: this.reviewFilter,
          key: "RecordId",
        }
      );
    },
    columns() {
      return [
        {
          screenField: "Item",
          label: "Product",
          gridDataField: "ProductDescription1",
          filterDataField: "ProductDescription1",
          sortOrder: "asc",
          sortIndex: 0,
          allowHiding: false,
          cellTemplateRecord: {
            primary: [
              {
                dataField: "ProductDescription1",
              },
            ],
            secondary: [
              {
                dataField: "ProductCode",
              },
            ],
          },
          exportFields: [
            {
              dataField: "ProductCode",
              label: "Product Code",
            },
            {
              dataField: "ProductDescription1",
              label: "Product Description 1",
            },
            {
              dataField: "ProductDescription2",
              label: "Product Description 2",
            },
            {
              dataField: "ProductDescription3",
              label: "Product Description 3",
            },
          ],
          minWidth: 200,
          filterIndex: 1,
        },
        {
          screenField: "Branch",
          minWidth: 200,
        },
        {
          caption: "UM",
          dataField: "UnitOfMeasureCode",
          width: ColumnWidth.UnitOfMeasure.width,
          minWidth: ColumnWidth.UnitOfMeasure.minWidth,
        },
        {
          screenField: "OnHand",
          dataField: "OnHand",
          width: ColumnWidth.Price4DecimalPoints.width,
          minWidth: ColumnWidth.Price4DecimalPoints.minWidth,
        },
        {
          caption: "Count",
          dataField: "Count",
          width: ColumnWidth.Price4DecimalPoints.width,
          minWidth: ColumnWidth.Price4DecimalPoints.minWidth,
          format: "##,###,###,###,##0.0000;(##,###,###,###,##0.0000)",
          cellTemplate: "Decimal",
          calculateCellValue: (e) => {
            if (e.CountStatus == "Pending") return 0;

            return e.Count;
          },
        },

        {
          caption: "Variance",
          dataField: "Variance",
          width: ColumnWidth.Price4DecimalPoints.width,
          minWidth: ColumnWidth.Price4DecimalPoints.minWidth,
          format: "##,###,###,###,##0.0000;(##,###,###,###,##0.0000)",
          cellTemplate: "Decimal",
        },
        {
          caption: "Std Cost (AUD)",
          dataField: "StandardCost",
          width: ColumnWidth.Price4DecimalPoints.width,
          minWidth: ColumnWidth.Price4DecimalPoints.minWidth,
          format: "$ ##,###,###,###,##0.0000;($ ##,###,###,###,##0.0000)",
          cellTemplate: "DecimalCell",
        },
        {
          caption: "Count (AUD)",
          dataField: "CountAmount",
          width: ColumnWidth.Price4DecimalPoints.width,
          minWidth: ColumnWidth.Price4DecimalPoints.minWidth,
          format: "$ ##,###,###,###,##0.0000;($ ##,###,###,###,##0.0000)",
          cellTemplate: "Decimal",
          visible: false,
        },
        {
          caption: "On Hand (AUD)",
          dataField: "OnHandAmount",
          width: ColumnWidth.Price4DecimalPoints.width,
          minWidth: ColumnWidth.Price4DecimalPoints.minWidth,
          format: "$ ##,###,###,###,##0.0000;($ ##,###,###,###,##0.0000)",
          cellTemplate: "Decimal",
        },
        {
          screenField: "VarianceAmount",
          caption: "Variance (AUD)",
          dataField: "VarianceAmount",
          width: ColumnWidth.Price4DecimalPoints.width,
          minWidth: ColumnWidth.Price4DecimalPoints.minWidth,
          //   format: "###,##0.00",
        },
        {
          caption: "Variance %",
          dataField: "VariancePcnt",
          width: ColumnWidth.Quantity2DecimalPoints.width,
          minWidth: ColumnWidth.Quantity2DecimalPoints.minWidth,
          format: "##0.00 '%';(##0.00 '%')",
          cellTemplate: "Decimal",
        },
        {
          caption: "",
          dataField: "CountStatus",
          width: 50,
          allowEditing: false,
          cellTemplate: "count-status-template",
        },
        {
          screenField: "GridRecordEllipsisMenu",
          //   cssClass: "padding-7",
          width: 50,
          allowSearch: false,
          ellipsisMenu: {
            itemAction: (data) => {
              var items = [
                {
                  menuText: "Exclude Selected",
                  data: data,
                  //   clickAction: this.excludeLine,
                },
              ];

              return items;
            },
            keyExpr: "menuText",
            displayExpr: "menuText",
          },
          // makeDisabled: (data) => {
          //   if (!data) return true;
          // },
          dropDownOptions: {
            width: 120,
          },
          visible: this.isEditable && this.getStocktakeManagementAccess,
        },
      ];
    },
    getStocktakeManagementAccess() {
      return FeatureStorage.checkPermission("StocktakeManagement", "create");
    },
    getUserAccess() {
      return (
        FeatureStorage.checkPermission("StocktakeManagement", "create") ||
        FeatureStorage.checkPermission("StocktakeCount", "create")
      );
    },

    getFilterFormItems() {
      return [
        {
          screenField: "Name",
          filterLabel: "Product",
          filterDataField: "ProductSearchText",
          type: "Text",
          filterIndex: 1,
        },
        {
          screenField: "Variance",
          type: "Integer",
          intOperators: true,
          dataField: "VarianceAbs",
          filterLabel: "Variance (Count)",
        },
        {
          screenField: "VarianceAmount",
          dataField: "VarianceAmountAbs",
          type: "Integer",
          intOperators: true,
          filterLabel: "Variance (AUD)",
        },
        {
          screenField: "Supplier",
          filterIndex: 2,
          filterDataField: "SupplierList",
          filterOperator: "contains",
          allowSearch: false,
        },
        {
          screenField: "ProductCategory1",
          label: "Category 1",
          filterLabel: "Category 1",
          filterIndex: 3,
        },
        {
          screenField: "ProductCategory2",
          label: "Category 2",
          filterLabel: "Category 2",
          filterIndex: 4,
        },
        {
          screenField: "ProductCategory3",
          label: "Category 3",
          filterLabel: "Category 3",
          filterIndex: 5,
        },
        {
          screenField: "ProductCategory4",
          label: "Category 4",
          filterLabel: "Category 4",
          filterIndex: 6,
        },
        {
          screenField: "ProductType",
          label: "Type",
          filterLabel: "Type",
          filterIndex: 7,
        },
        {
          screenField: "ProductSize",
          label: "Size",
          filterLabel: "Size",
          filterIndex: 8,
        },
        {
          screenField: "OrderStatus",
          filterLabel: "Count Status",
          gridDataField: "FriendlyStatus",
          filterDataField: "CountStatus",
          filterIndex: 9,
          dataSourceConfig: {
            data: ["Pending"],
          },
          visible: true,
        },
      ];
    },
    reviewFilter() {
      return [
        ["StocktakeCode", "=", parseInt(this.baseView.id)],
        "and",
        ["Status", "<>", "X"],
      ];
    },

    // Footer
    getFooterItems() {
      return [
        {
          screenField: "OnHand",
          location: "right",
          value: 0,
          dataField: "OnHandAmount",
          format: "Decimal-16-4C",
          label: `Total On Hand (AUD)`,
        },
        {
          screenField: "VarianceAmount",
          location: "right",
          value: 0,
          dataField: "VarianceAmount",
          //   format: "Decimal-16-4C",
          label: `Total Variance (AUD)`,
        },
        {
          label: "Variance %",
          dataField: "VariancePcnt",
          location: "right",
          value: 0,
          format: "Decimal-16-2P",
          customCalc: (headerData, gridItems) => {
            let totalVariance = 0;
            let totalOnhand = 0;
            gridItems.forEach((x) => {
              totalVariance += parseFloat(x.VarianceAmount).roundNumber(4);
              totalOnhand += parseFloat(x.OnHandAmount).roundNumber(4);
            });

            if (totalVariance && totalOnhand)
              return (totalVariance / totalOnhand) * 100;
            else return 0;
          },
        },
      ];
    },
    footerRightItems() {
      // return this.footerItems.filter((x) => x.location == "right");
      var rightItems = this.footerItems.filter(
        (x) =>
          x.location == "right" &&
          (x.hasOwnProperty("visible") ? x.visible : true)
      );

      return rightItems
        .map((c) => {
          let globalField = { ...getGlobalField(c.screenField) };

          if (Object.keys(globalField).length == 0) return c;

          if (c.value != undefined) globalField.value = c.value;
          if (c.dataField != undefined) globalField.dataField = c.dataField;
          if (c.label != undefined) globalField.label = c.label;
          if (c.format != undefined) globalField.format = c.format;

          if (globalField) return globalField;
          else return c;
        })
        .filter((x) => x != null);
    },
    isEditable() {
      return this.Status != "C" && this.Status != "X";
    },

    getHeaderToolbar() {
      return {
        audit: false,
        status: this.baseView.mode != "create",
        notes: false,
        save: false,
        customButtons:
          this.baseView.mode !== "create"
            ? [
                {
                  text: "Cancel",
                  type: "normal",
                  onClick: this.cancelStockTake,
                  visible: this.isEditable,
                  disabled: !this.getStocktakeManagementAccess,
                },
                {
                  text: "Commit",
                  type: "normal",
                  class: "save-btn-icon",
                  onClick: this.commitStockTake,
                  visible: this.isEditable,
                  disabled: !this.getStocktakeManagementAccess,
                },
              ]
            : undefined,
      };
    },
  },
  methods: {
    recallFromCount(value) {
      if (value) {
        var dataGrid = this.$refs["dx-data-grid-comp"].instance;
        dataGrid.refresh();
      }
    },
    onCountHyperlinkClicked(rowData) {
      let data = rowData.data;
      this.countData = {
        ProductCode: data.ProductCode,
        ProductDescription: data.ProductDescription1,
        StocktakeCode: data.StocktakeCode,
        BranchCode: data.BranchCode,
        BranchDescription: data.BranchDescription,
        Status: data.Status,
      };
      this.visibleCountInfo = true;
    },
    closeFilterPanel() {
      this.showRightContainer = !this.showRightContainer;

      var item = document.getElementById("filter-btn");
      item.classList.remove("dx-state-invisible");

      this.$nextTick(() => {
        this.reSizeGrid();
      });
    },
    onStocktakeGridSelectionChanged(e) {
      this.stocktakeGridSelectedData = e.selectedRowsData;
    },
    onItemClickCallBack(data, e) {
      if (e.event) {
        this.excludeLine([data.data.RecordId._value]);
      }
    },
    excludeLines() {
      if (this.stocktakeGridSelectedData.length === 0) {
        this.displayValidationSummary([
          {
            message: "Select one or more lines and try again.",
          },
        ]);
      } else {
        let lineRecordIds = [];
        this.stocktakeGridSelectedData.forEach((element) => {
          lineRecordIds.push(element.RecordId._value);
        });
        this.excludeLine(lineRecordIds);
      }
    },
    async excludeLine(RecordIds) {
      let confirmExclude = await this.$confirm(
        "Are you sure you would like to exclude the selected Item(s) from the Stocktake?",
        "Exclude Stocktake"
      );

      if (confirmExclude) {
        let payload = {
          StocktakeCode: this.baseView.id,
          StocktakeRecordIds: RecordIds,
        };
        this.showLoading(true);
        var dataGrid = this.$refs["dx-data-grid-comp"].instance;
        await StocktakeReviewService.setLineExclude(payload)
          .then((res) => {
            if (res) this.showLoading(false);
            dataGrid.clearSelection();
            this.$nextTick(() => {
              dataGrid.refresh();
            });
          })
          .catch((err) => {
            this.showLoading(false);
            this.displayServerError(err, "Exclude Stocktake line Failed");
          });
      }
    },
    onContentReady(e) {
      if (e) {
        this.setFooterItems(e.component.getDataSource().items());
        this.addGridNoResults(e);
      }
    },
    backButtonFunction() {
      this.$router
        .push({
          path: this.backButtonRoute,
        })
        .then(() => {
          this.$store.dispatch("dataContext/setDataViewType", undefined);
        });
    },
    toolbarPreparing(e) {
      var toolbarItems = e.toolbarOptions.items;
      var searchPanel = toolbarItems.find((item) => {
        return item.name === "searchPanel";
      });
      var columnChooserButton = toolbarItems.find((item) => {
        return item.name === "columnChooserButton";
      });

      let itemsToRemoveFromToolBar = ["searchPanel", "columnChooserButton"];

      e.toolbarOptions.items = toolbarItems.filter(
        (item) => !itemsToRemoveFromToolBar.includes(item.name)
      );

      searchPanel.location = "after";
      e.toolbarOptions.items.unshift(
        // searchPanel,
        columnChooserButton,
        {
          location: "after",
          locateinmenu: "auto",
          widget: "dxDropDownButton",
          visible: this.baseView.mode !== "create",
          options: {
            tabIndex: -1,
            validationGroup: "formsData",
            icon: "overflow",
            onItemClick: () => {
              this.excludeLines();
            },
            items: [
              {
                text: "Exclude Selected",
              },
            ],
            keyExpr: "text",
            displayExpr: "text",
            showArrowIcon: false,
            dropDownOptions: {
              minWidth: 120,
            },
            visible: this.isEditable && this.getStocktakeManagementAccess,
          },
        },
        {
          location: "after",
          locateinmenu: "auto",
          widget: "dxButton",
          options: {
            tabIndex: -1,
            validationGroup: "formsData",
            icon: "filter",
            elementAttr: { id: "filter-btn" },
            onClick: () => {
              this.showRightContainer = !this.showRightContainer;
              var item = document.getElementById("filter-btn");

              this.$nextTick(() => {
                item.classList.add("dx-state-invisible");
                setTimeout(() => {
                  this.reSizeGrid();

                  let filter = document.getElementById("filter-btn");
                  filter.classList.add("dx-state-invisible");
                }, 100);
              });
            },
            visible: !this.showRightContainer,
          },
        }
      );
    },
    reSizeGrid() {
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;

      dataGrid.beginUpdate();
      dataGrid.updateDimensions();
      dataGrid.endUpdate();
    },

    setGridPanelFilter(e, valueChanged) {
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      // valueChanged === true means filter is applied.
      if (!valueChanged) return;
      else {
        if (e.length > 0) {
          let filter = [];
          filter.push([...this.reviewFilter]);
          filter.push("and");
          filter.push([...e]);

          dataGrid.filter(filter);
        } else {
          dataGrid.filter([...this.reviewFilter]);
        }
      }
    },

    // Footer
    async setFooterItems(gridItems) {
      // this.showFooter = false;
      this.getFooterItems
        .filter((x) => (x.hasOwnProperty("visible") ? x.visible : true))
        .forEach((x) => {
          x.value = this.updateValue(x.dataField, gridItems);
          if (x.customCalc) x.value = x.customCalc(this.model, gridItems);
        });
      this.footerItems = this.getFooterItems;
      this.showFooter = true;
    },
    updateValue(dataField, items) {
      let result = 0;
      if (items.length > 0)
        items.forEach((x) => {
          result += parseFloat(x[dataField]).roundNumber(4);
        });

      return result;
    },

    // Toolbar Actions
    async cancelStockTake() {
      let confirmCancel = await this.$confirm(
        "Are you sure you want to cancel this stocktake ?",
        "Cancel Stocktake"
      );

      if (confirmCancel) {
        let payload = { Code: this.baseView.id };
        this.showLoading(true);
        await StockTakeListService.cancelStockTake(payload)
          .then(() => {
            this.showLoading(false);
            this.setIgnoreRouteChangeConfirmation();
            this.$router.push({
              path: "/stocktake",
            });
          })
          .catch((err) => {
            this.showLoading(false);
            this.displayServerError(err, "Cancel Stocktake line Failed");
          });
      }
    },
    async commitStockTake() {
      this.showLoading(true);
      let payload = { Code: this.baseView.id };

      await StockTakeListService.commitStockTake(payload)
        .then(() => {
          this.showLoading(false);

          this.$notify(
            `Stocktake ${this.baseView.id} Successfully Commited`,
            "success"
          );
          this.setIgnoreRouteChangeConfirmation();
          this.$router.push({
            path: "/stocktake",
          });
        })
        .catch((err) => {
          this.showLoading(false);
          this.displayServerError(err, "Stocktake Commit Failed");
        });
    },
  },
};
</script>
